import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { compose } from 'recompose'
import { labels as labelsApi } from 'redux/modules/api'
import { logError, modal, notification } from 'redux/modules/app'
import { get } from 'lodash'

function decorator(config = {}) {
  return ComposedComponent => {
    const enhance = compose(
      withRouter,
      connect(
        ({ api, auth, app }) => ({
          labels: api.labels,
          user: auth.user,
          activeOrganizationId: auth.organization.id,
          commonLimit: app.commonLimit
        }),
        {
          ...labelsApi,
          modal,
          push,
          notification,
          logError
        }
      )
    )

    class Labels extends Component {
      static propTypes = {
        labels: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
        activeOrganization: PropTypes.object.isRequired,
        activeOrganizationId: PropTypes.object.isRequired,
        local: PropTypes.object.isRequired,
        params: PropTypes.object,
        decoOrganizationId: PropTypes.number,
        getPersonLabels: PropTypes.func.isRequired,
        getOrganizationLabels: PropTypes.func.isRequired,
        getContactLabels: PropTypes.func.isRequired,
        getAwardLabels: PropTypes.func.isRequired,
        getLabels: PropTypes.func.isRequired,
        getLabelsInHierarchy: PropTypes.func.isRequired,
        getAvailableLabels: PropTypes.func.isRequired,
        searchLabels: PropTypes.func.isRequired,
        assignPersonLabelBulk: PropTypes.func.isRequired,
        unassignPersonLabelBulk: PropTypes.func.isRequired,
        createLabel: PropTypes.func.isRequired,
        deleteLabel: PropTypes.func.isRequired,
        updateLabel: PropTypes.func.isRequired,
        assignPersonLabel: PropTypes.func.isRequired,
        unassignPersonLabel: PropTypes.func.isRequired,
        assignOrganizationLabel: PropTypes.func.isRequired,
        unassignOrganizationLabel: PropTypes.func.isRequired,
        assignOrganizationLabelBulk: PropTypes.func.isRequired,
        unassignOrganizationLabelBulk: PropTypes.func.isRequired,
        assignContactLabel: PropTypes.func.isRequired,
        unassignContactLabel: PropTypes.func.isRequired,
        assignContactLabelBulk: PropTypes.func.isRequired,
        unassignContactLabelBulk: PropTypes.func.isRequired,
        assignAwardLabel: PropTypes.func.isRequired,
        unassignAwardLabel: PropTypes.func.isRequired,
        assignAwardLabelBulk: PropTypes.func.isRequired,
        unassignAwardLabelBulk: PropTypes.func.isRequired,
        notification: PropTypes.func.isRequired,
        decoFilter: PropTypes.string,
        modal: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        commonLimit: PropTypes.number,
        setParams: PropTypes.func
      }

      static defaultProps = {
        params: {}
      }

      componentDidMount() {
        const { labels } = this.props

        const { commonLimit, setParams } = this.props

        if (config.useCommonLimit && commonLimit) {
          setParams({ limit: commonLimit })
        }

        config.list && this.handleGetLabels(labels, this.handleGetBaseFilter())
      }

      componentWillReceiveProps(nextProps) {
        const { labels } = this.props

        const { filter, limit, offset, sort, invalidate } = nextProps.labels

        if (
            labels.filter !== filter ||
            labels.limit !== limit ||
            labels.offset !== offset ||
            labels.sort !== sort ||
            labels.invalidate !== invalidate
        ) {
          config.list && this.handleGetLabels(nextProps.labels, this.handleGetBaseFilter())
        }
      }

      handleFetchCurrentOrganizationLabels = () => {
        const { labels } = this.props

        this.handleGetCurrentOrganizationLabels(labels, this.handleGetBaseFilter())
      }

      handleFetchAvailableLabels = (personId, orgId, contactId, awardId) => {
        const data = { personId, orgId, contactId, awardId }

        return this.props.getAvailableLabels(data)
      }

      handleGetLabelsForOrganization = (labels, filter) => {
        const orgId = this.handleGetOrganizationId()

        filter += `organization.id=${orgId}`
        return this.handleGetLabelsInHierarchy(labels, filter)
      }

      handleGetCurrentOrganizationLabels = (labels, filter) => {
        const orgId = this.handleGetOrganizationId()

        filter += `organization.id=${orgId}`
        return this.handleGetLabels(labels, filter)
      }

      handleGetLabels = (labels, filter) => {
        if (config.disableLimit) {
          labels.limit = undefined
        }

        const orgId = this.handleGetOrganizationId()

        return this.props.getLabels(labels, orgId, filter)
      }

      handleGetLabelsInHierarchy = (labels, filter) => {
        if (config.disableLimit) {
          labels.limit = undefined
        }

        return this.props.getLabelsInHierarchy(labels, filter)
      }

      handleGetBaseFilter = () => {
        const { decoFilter } = this.props

        switch (decoFilter || config.filter) {
          case 'orgLabels':
            return `organization.id=${this.handleGetOrganizationId()}`
          case 'orgPersonLabels':
            return `organization.id=${this.handleGetOrganizationId()},category.value=PERSON`
          case 'childOrgLabels':
            return `organization.flatHierarchy.parentOrganization.id=${get(this.props, 'activeOrganizationId')}`
          default:
            return ''
        }
      }

      handleGetOrganizationId = () => {
        return get(this.props, 'match.params.id') || get(this.props, 'activeOrganizationId')
      }

      handleGetPersonLabels = id => {
        return this.props.getPersonLabels(id)
            .catch(this.handleError)
      }

      handleGetOrganizationLabels = id => {
        return this.props.getOrganizationLabels(id)
            .catch(this.handleError)
      }

      handleGetContactLabels = id => {
        return this.props.getContactLabels(id)
            .catch(this.handleError)
      }

      handleGetAwardLabels = id => {
        return this.props.getAwardLabels(id)
          .catch(this.handleError)
      }

      handleCreateLabel = (orgId, values) => {
        return this.props.createLabel(orgId, values)
            .then(this.handleCreateSuccess)
            .catch(this.handleError)
      }

      handleDeleteLabel = id => {
        return this.props.deleteLabel(id)
            .then(this.handleDeleteSuccess)
            .catch(this.handleError)
      }

      handleUpdateLabel = (id, values) => {
        return this.props.updateLabel(id, values)
            .then(this.handleDeleteSuccess)
            .catch(this.handleError)
      }

      handleAssignPersonLabel = (personId, labelId) => {
        return this.props.assignPersonLabel(personId, labelId)
            .then(this.handleAssignSuccess)
            .catch(this.handleError)
      }

      handleUnassignPersonLabel = id => {
        return this.props.unassignPersonLabel(id)
            .then(this.handleDeleteSuccess)
            .catch(this.handleError)
      }

      handleAssignOrganizationLabel = (orgId, labelId) => {
        return this.props.assignOrganizationLabel(orgId, labelId)
            .then(this.handleAssignSuccess)
            .catch(this.handleError)
      }

      handleUnassignOrganizationLabel = id => {
        return this.props.unassignOrganizationLabel(id)
            .then(this.handleDeleteSuccess)
            .catch(this.handleError)
      }

      handleAssignContactLabel = (contactId, labelId) => {
        return this.props.assignContactLabel(contactId, labelId)
            .then(this.handleAssignSuccess)
            .catch(this.handleError)
      }

      handleUnassignContactLabel = id => {
        return this.props.unassignContactLabel(id)
            .then(this.handleDeleteSuccess)
            .catch(this.handleError)
      }

      handleAssignAwardLabel = (awardId, labelId) => {
        return this.props.assignAwardLabel(awardId, labelId)
          .then(this.handleAssignSuccess(false))
          .catch(this.handleError)
      }

      handleUnassignAwardLabel = id => {
        return this.props.unassignAwardLabel(id)
          .then(this.handleDeleteSuccess(false))
          .catch(this.handleError)
      }

      getLabels = (orgId, labelType, params = {}) => {
        this.props.getLabels(orgId, labelType, params)
            .catch(this.handleError)
      }

      searchLabels = (labelType, params = {}) => {
        this.props.searchLabels(labelType, params, this.props.activeOrganizationId)
            .catch(this.handleError)
      }

      handleCreateSuccess = () => {
        this.props.notification('Label has been created.', {
          status: 'success',
          intlKey: 'notifications.label_has_been_created'
        })
        this.props.modal(null)
        this.handleRefresh()
      }

      handleAssignSuccess = (refresh = true) => {
        this.props.notification('Label has been assigned.', {
          status: 'success',
          intlKey: 'notifications.label_has_been_assigned'
        })
        this.props.modal(null)
        refresh && this.handleRefresh()
      }

      handleDeleteSuccess = (refresh = true) => {
        this.props.notification('Label has been removed.', {
          status: 'success',
          intlKey: 'notifications.label_has_been_removed'
        })
        this.props.modal(null)
        refresh && this.handleRefreshDelete()
      }

      handleUpdateSuccess = () => {
        this.props.notification('Label has been updated.', {
          status: 'success',
          intlKey: 'notifications.label_has_been_updated'
        })
        this.props.modal(null)
        this.handleRefresh()
      }

      handleError = err => {
        console.log('error', err.body && err.body.userMessage)
        this.props.logError(err)
      }

      handleRefresh = () => {
        this.handleFetchCurrentOrganizationLabels()
      }

      handleRefreshDelete = () => {
        this.handleGetLabels(this.props.labels, this.handleGetBaseFilter())
      }

      render() {
        const props = {
          ...this.props,
          local: undefined
        }

        return (
          <ComposedComponent
            {...props}
            onFetchAvailableLabels={this.handleFetchAvailableLabels}
            onFetchCurrentOrganizationLabels={this.handleFetchCurrentOrganizationLabels}
            onGetPersonLabels={this.handleGetPersonLabels}
            onGetOrganizationLabels={this.handleGetOrganizationLabels}
            onGetContactLabels={this.handleGetContactLabels}
            onGetAwardLabels={this.handleGetAwardLabels}
            onCreateLabel={this.handleCreateLabel}
            onDeleteLabel={this.handleDeleteLabel}
            onUpdateLabel={this.handleUpdateLabel}
            onAssignPersonLabel={this.handleAssignPersonLabel}
            onUnassignPersonLabel={this.handleUnassignPersonLabel}
            onAssignOrganizationLabel={this.handleAssignOrganizationLabel}
            onUnassignOrganizationLabel={this.handleUnassignOrganizationLabel}
            onAssignContactLabel={this.handleAssignContactLabel}
            onUnassignContactLabel={this.handleUnassignContactLabel}
            onAssignAwardLabel={this.handleAssignAwardLabel}
            onUnassignAwardLabel={this.handleUnassignAwardLabel}
            onGetLabelsInHierarchy={this.handleGetLabelsInHierarchy}
          />
        )
      }
    }

    return enhance(Labels)
  }
}

export default decorator
